import * as S from './CommunitySection.styles'
import { ReactComponent as UnmuteIcon } from 'images/homePage/unmute.svg'
import { ReactComponent as MuteIcon } from 'images/homePage/mute.svg'
import { usePlaybackData } from '../../../../components/PlaybackProvider/PlaybackContext'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { useEffect, useRef, useState } from 'react'
import useScreenSize from '../../../../hooks/useScreenSize'
import { type VideoRefType } from '../HomePageVideo/HomePageVideo'
import { attemptPlayVideo } from '../../../../utils/videoUtils'
import 'components/SlickSlider/SlickSlider.scss'
import { TransformedSlider } from './CommunitySection.styles'
import { useTranslation } from 'react-i18next'
import { useNoSSR } from 'utils/NoSSR'

export type VideoData = {
  handle: string
  productName?: string
  name: string
  cldId: string
  cldWidth?: number
  folder?: string
}

export default function CommunitySection({ items }: { items: VideoData[] }) {
  const isClient = useNoSSR()
  const { t } = useTranslation()

  const isLargeScreen = useScreenSize('lg')
  const { muted, toggleMute } = usePlaybackData()

  const videoComponents = items.map((video, index) => (
    <CommunityItem
      key={video.name}
      toggleMute={toggleMute}
      muted={muted}
      storyIndex={index}
      {...video}
    />
  ))

  return (
    <S.Container>
      <S.Title>{t('homepage.community.title')}</S.Title>
      {isLargeScreen && isClient ? (
        <S.ItemsContainer>
          <TransformedSlider
            infinite
            slidesToShow={4}
            slidesToScroll={1}
            prevArrow={
              <S.Arrow>
                <S.LeftArrowIcon />
              </S.Arrow>
            }
            nextArrow={
              <S.Arrow>
                <S.RightArrowIcon />
              </S.Arrow>
            }
          >
            {videoComponents}
          </TransformedSlider>
        </S.ItemsContainer>
      ) : (
        <S.ScrollContainer>
          <S.ItemsContainer>{videoComponents}</S.ItemsContainer>
        </S.ScrollContainer>
      )}
    </S.Container>
  )
}

function CommunityItem({
  cldId,
  cldWidth,
  handle,
  productName,
  toggleMute,
  muted,
  name,
  storyIndex,
  folder,
}: VideoData & {
  toggleMute: () => void
  muted: boolean
  storyIndex: number
}) {
  const isLargeScreen = useScreenSize('lg')
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef<VideoRefType>(null)

  useEffect(() => {
    if (videoRef.current?.videoRef.current) {
      if (isHovered) {
        attemptPlayVideo(videoRef.current.videoRef.current, name)
      } else {
        videoRef.current.videoRef.current.pause()
        videoRef.current.videoRef.current.currentTime = 0
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered])

  const handleMouseOver = () => {
    setIsHovered(true)
  }

  const handleMouseOut = () => {
    setIsHovered(false)
  }

  return (
    <S.ItemContainer>
      <S.Video
        ref={videoRef}
        cldId={cldId}
        folder={folder}
        name={name}
        type="Story"
        analyticProps={{ 'Story Index': storyIndex.toString() }}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        shouldAutoPlay={!isLargeScreen}
        cldWidth={cldWidth}
      >
        {(!isLargeScreen || (isLargeScreen && isHovered)) && (
          <S.MuteToggle
            onClick={() => {
              const eventName = muted
                ? EVENT_NAMES.HOMEPAGE_STORY_UNMUTED
                : EVENT_NAMES.HOMEPAGE_STORY_MUTED
              analytics.track(eventName, { Index: storyIndex, Name: name })

              toggleMute()
            }}
          >
            {muted ? <UnmuteIcon /> : <MuteIcon />}
          </S.MuteToggle>
        )}
      </S.Video>
      <S.VideoDetails>
        <S.HandleText>@{handle}</S.HandleText>
        <S.ProductText>{productName}</S.ProductText>
      </S.VideoDetails>
    </S.ItemContainer>
  )
}
